<template>
  <div class="pe-wrap">
    <div class="pe-card">
      <!-- Icon -->
      <div class="pe-icon" :class="{ success: step === 'done', error: step === 'error' }">
        <svg v-if="step === 'done'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        <svg v-else-if="step === 'error'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
        <div v-else class="pe-spinner"></div>
      </div>

      <!-- Text -->
      <div class="pe-title">{{ title }}</div>
      <div class="pe-sub">{{ subtitle }}</div>

      <!-- Progress dots (loading only) -->
      <div v-if="step === 'loading'" class="pe-dots">
        <span></span><span></span><span></span>
      </div>

      <!-- Error back-link -->
      <a v-if="step === 'error'" href="/login" class="pe-back">← Back to Login</a>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PartnerEntry',

  data() {
    return {
      step:     'loading',   // 'loading' | 'done' | 'error'
      title:    'Entering workspace…',
      subtitle: 'Setting up your session, please wait.',
    };
  },

  created() {
    this.setup();
  },

  methods: {
    async setup() {
      try {
        const q = this.$route.query;

        // ── Read handoff params ──────────────────────────────────
        const token   = q.t    ? atob(q.t)    : null;
        const domain  = q.d    ? atob(q.d)    : null;   // "cmp_xyz"
        const userId  = q.uid  ? atob(q.uid)  : null;
        const uname   = q.un   ? atob(q.un)   : null;
        const uemail  = q.ue   ? atob(q.ue)   : null;

        if (!token || !domain) throw new Error('Missing workspace session parameters.');

        // ── Clear the token from the address bar immediately ────
        // so it doesn't linger in browser history
        if (window.history && window.history.replaceState) {
          window.history.replaceState({}, document.title, '/partner_entry');
        }

        this.subtitle = 'Loading workspace data…';

        // ── Set workspace session in localStorage ────────────────
        localStorage.setItem('TOKEN',      token);
        localStorage.setItem('USER_ID',    userId  || '');
        localStorage.setItem('USER_NAME',  uname   || '');
        localStorage.setItem('USER_EMAIL', uemail  || '');
        localStorage.setItem('domain',     domain);

        // ── Fetch entity list for this workspace ─────────────────
        const featRes = await this.$api.get(`get_features_by_role/${userId}`, {
          headers: { Authorization: `Bearer ${token}`, prefix: domain },
        });

        const entityList = featRes.data?.data?.[0]?.entity_list || [];

        if (!entityList.length) {
          throw new Error('No entity found for this workspace. Contact your administrator.');
        }

        localStorage.setItem('entitydata',  JSON.stringify(entityList));
        localStorage.setItem('ENTITY_ID',   entityList[0].id);
        localStorage.setItem('entity_name', entityList[0].name);
        localStorage.setItem('ENT_FINYEAR', entityList[0].financial_year || '__NA__');

        // ── Success — navigate to dashboard ──────────────────────
        this.step     = 'done';
        this.title    = 'Workspace ready';
        this.subtitle = `Welcome, ${uname || 'user'}! Redirecting…`;

        setTimeout(() => this.$router.replace('/checklist_dashboard'), 800);

      } catch (err) {
        console.error('[PartnerEntry]', err);
        this.step     = 'error';
        this.title    = 'Access failed';
        this.subtitle = err.message || 'Could not enter the workspace. Please try again.';
      }
    },
  },
};
</script>

<style scoped>
.pe-wrap {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%);
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
}

.pe-card {
  background: #fff;
  border-radius: 20px;
  padding: 48px 40px 40px;
  text-align: center;
  box-shadow: 0 8px 40px rgba(59, 76, 192, 0.10);
  max-width: 400px;
  width: 90%;
}

/* Icon circle */
.pe-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #EEF0FD;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  transition: background 0.3s;
}
.pe-icon svg { width: 32px; height: 32px; color: #3B4CC0; }
.pe-icon.success { background: #DCFCE7; }
.pe-icon.success svg { color: #16a34a; }
.pe-icon.error { background: #FEE2E2; }
.pe-icon.error svg { color: #dc2626; }

/* Spinner */
.pe-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #C7CCEF;
  border-top-color: #3B4CC0;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.pe-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1E1E2F;
  margin-bottom: 8px;
}
.pe-sub {
  font-size: 0.875rem;
  color: #9A96A8;
  margin-bottom: 20px;
}

/* Progress dots */
.pe-dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 8px;
}
.pe-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #C7CCEF;
  animation: bounce 1.2s ease-in-out infinite;
}
.pe-dots span:nth-child(1) { animation-delay: 0s; }
.pe-dots span:nth-child(2) { animation-delay: 0.2s; }
.pe-dots span:nth-child(3) { animation-delay: 0.4s; }
@keyframes bounce {
  0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
  40%           { transform: scale(1.1); opacity: 1; }
}

.pe-back {
  display: inline-block;
  margin-top: 8px;
  font-size: 0.85rem;
  color: #3B4CC0;
  text-decoration: none;
  font-weight: 600;
}
.pe-back:hover { text-decoration: underline; }
</style>
